/**
 * What the four catalog forms pick from — vendors, categories, units — and the
 * company's markup, read once per form from `GET /catalog-admin/lookups`.
 *
 * And the two things a form may make without leaving: a category (the combo's
 * last row, as Materials always could) and a unit (`UnitSelect`'s last row,
 * §6.1's `Create a new unit…`). Each is added to the lookups here, so the
 * field shows it a moment later without a refetch.
 */
import { useCallback, useEffect, useState } from 'react'
import { request } from '../../lib/api.js'
import type { UnitOption } from './catalog-parts.js'

export interface CatalogLookups {
  vendors: { id: string; name: string }[]
  categories: { id: string; name: string }[]
  units: UnitOption[]
  companyMarkupPct: number
  addUnit: (unit: UnitOption) => void
  addCategory: (category: { id: string; name: string }) => void
}

export function useCatalogLookups(): CatalogLookups {
  const [state, setState] = useState<Omit<CatalogLookups, 'addUnit' | 'addCategory'>>({
    vendors: [], categories: [], units: [], companyMarkupPct: 35,
  })
  useEffect(() => {
    let live = true
    void request<{
      vendors: { id: string; name: string }[]
      categories: { id: string; name: string }[]
      units: UnitOption[]
      companyMarkupPct?: number
    }>('/catalog-admin/lookups').then((payload) => {
      if (!live) return
      setState({
        vendors: payload.vendors,
        categories: payload.categories,
        units: payload.units,
        companyMarkupPct: Number(payload.companyMarkupPct ?? 35),
      })
    }).catch(() => undefined)
    return () => { live = false }
  }, [])
  const addUnit = useCallback((unit: UnitOption) =>
    setState((was) => ({ ...was, units: [...was.units, unit] })), [])
  const addCategory = useCallback((category: { id: string; name: string }) =>
    setState((was) => ({ ...was, categories: [...was.categories, category] })), [])
  return { ...state, addUnit, addCategory }
}

/**
 * A unit named after what was typed. Its short form is the name, lower-cased
 * and squeezed to the twelve characters a code may hold; it is a count until
 * somebody files it under something else in Catalog setup.
 */
export async function createUnit(
  name: string, lookups: CatalogLookups, onProblem: (message: string) => void,
): Promise<UnitOption | null> {
  const trimmed = name.trim()
  if (!trimmed) {
    onProblem('Type the unit’s name in the search box first, then create it.')
    return null
  }
  const code = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 12) || 'unit'
  try {
    const { id } = await request<{ id: string }>('/catalog-admin/units', {
      method: 'POST', body: { name: trimmed, code, kind: 'count' },
    })
    const unit = { id, code, name: trimmed, kind: 'count' }
    lookups.addUnit(unit)
    return unit
  } catch (cause) {
    onProblem(cause instanceof Error ? cause.message : 'That unit could not be made.')
    return null
  }
}

export async function createCategory(
  name: string, lookups: CatalogLookups, onProblem: (message: string) => void,
): Promise<{ id: string; name: string } | null> {
  try {
    const { id } = await request<{ id: string }>(
      '/catalog-admin/item-categories', { method: 'POST', body: { name } })
    lookups.addCategory({ id, name })
    return { id, name }
  } catch (cause) {
    onProblem(cause instanceof Error ? cause.message : 'That category could not be made.')
    return null
  }
}
